import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, forkJoin, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

export interface UserSiteAccess {
  id: string;
  userId: string;
  siteId: string;
  siteInstanceId: string;
  siteInstanceKey: string;
  siteInstanceName?: string;
  tenantId: string;
  createdAtUtc: string;
  modifiedAtUtc: string;
  tasksAuthorization: boolean;
  entitiesAuthorization: boolean;
  documentsAuthorization: boolean;
  invoicesAuthorization: boolean;
  messagesAuthorization: boolean;
  sharesAuthorization: boolean;
  relatedPartiesAuthorization: boolean;
  requestsAuthorization: boolean;
  bankingAuthorization: boolean;
  assetsAuthorization: boolean;
  investmentsAuthorization: boolean;
  informationAuthorization: boolean;
  loansAuthorization: boolean;
  user: {
    id: string;
    emailAddress: string;
    email?: string;
    name?: string;
    displayName?: string;
    fullName?: string;
    givenName?: string;
    surname?: string;
    status?: number;
    hasAcceptedTerms?: boolean;
    authorizations: Record<string, unknown>;
    isAdmin: boolean;
  };
}

export interface SiteEntity {
  id: string;
  name: string;
  type: string;
  entityCode?: string;
}

export interface EntityDetailSummary {
  id: string;
  name: string;
  type: string;
  entityCode?: string;
  totalEntities: number;
  totalResignedEntities: number;
  totalInvoices: number;
  totalShareClasses: number;
  totalBankAccounts: number;
  totalInvestments: number;
  totalAssets: number;
  totalLoans: number;
  totalDocuments: number;
}

export interface SiteUser {
  id: string;
  name: string;
  displayName?: string;
  email: string;
  initials: string;
  avatarColor: string;
  joinedDate?: string;
  status?: number; // 1 = Draft, 2 = PendingFirstLogin, 3 = Active, 4 = SyncError, 5 = Inactive
  termsAccepted?: boolean;
  authorizations?: {
    tasks: boolean;
    entities: boolean;
    documents: boolean;
    invoices: boolean;
    messages: boolean;
    shares: boolean;
    relatedParties: boolean;
    requests: boolean;
    banking: boolean;
    assets: boolean;
    investments: boolean;
    information: boolean;
    loans: boolean;
  };
}

export interface SiteDetails {
  siteId: string;
  siteInstanceId: string;
  userCount: number;
  entities: SiteEntity[];
  users: SiteUser[];
}

@Injectable({
  providedIn: 'root'
})
export class SiteDetailsService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = '/api';

  /**
   * Get all details for a site instance including entities and users
   */
  getSiteDetails(siteInstanceId: string): Observable<SiteDetails> {
    return this.http.get<SiteDetails>(`${this.apiUrl}/sites/instances/${siteInstanceId}/details`).pipe(
      catchError((error: HttpErrorResponse) =>
        throwError(() => new Error(
          error.error?.message || 'Failed to fetch site details'
        ))
      )
    );
  }

  /**
   * Get entities for a specific site instance
   */
  getSiteEntities(siteInstanceId: string): Observable<SiteEntity[]> {
    return this.http.get<Record<string, string>[]>(`${this.apiUrl}/sites/instances/${siteInstanceId}/entities`).pipe(
      map((entities: Record<string, string>[]) => entities.map(entity => ({
        id: entity.id || entity.Id,
        name: entity.name || entity.Name,
        type: entity.type || entity.Type || entity.entityType || entity.EntityType,
        entityCode: entity.entityCode || entity.EntityCode || entity.code || entity.Code
      }))),
      catchError((error: HttpErrorResponse) =>
        throwError(() => new Error(
          error.error?.message || 'Failed to fetch site entities'
        ))
      )
    );
  }

  /**
   * Get all users across all site instances for export
   */
  getAllSiteUsers(): Observable<UserSiteAccess[]> {
    return this.http.get<UserSiteAccess[]>(`${this.apiUrl}/sites/instances/users`).pipe(
      catchError((error: HttpErrorResponse) =>
        throwError(() => new Error(
          error.error?.message || 'Failed to fetch all site users'
        ))
      )
    );
  }

  /**
   * Get users for a specific site instance (raw API response)
   */
  getSiteUsersRaw(siteInstanceId: string): Observable<UserSiteAccess[]> {
    return this.http.get<UserSiteAccess[]>(`${this.apiUrl}/sites/instances/${siteInstanceId}/users`).pipe(
      catchError((error: HttpErrorResponse) =>
        throwError(() => new Error(
          error.error?.message || 'Failed to fetch site users'
        ))
      )
    );
  }

  /**
   * Get users for a specific site instance
   */
  getSiteUsers(siteInstanceId: string): Observable<SiteUser[]> {
    return this.http.get<UserSiteAccess[]>(`${this.apiUrl}/sites/instances/${siteInstanceId}/users`).pipe(
      map((userSiteAccess: UserSiteAccess[]) => userSiteAccess.map(usa => ({
        id: usa.user?.id || usa.userId,
        name: usa.user?.name || usa.user?.displayName || usa.user?.fullName || (usa.user?.givenName && usa.user?.surname ? `${usa.user.givenName} ${usa.user.surname}` : usa.user?.emailAddress) || usa.user?.email || 'Unknown User',
        displayName: usa.user?.displayName || undefined,
        email: usa.user?.emailAddress || usa.user?.email || '',
        initials: this.generateInitials(usa.user?.displayName || usa.user?.name || usa.user?.fullName || (usa.user?.givenName && usa.user?.surname ? `${usa.user.givenName} ${usa.user.surname}` : usa.user?.emailAddress) || usa.user?.email || 'Unknown User'),
        avatarColor: this.generateAvatarColor(usa.user?.emailAddress || usa.user?.email || ''),
        joinedDate: usa.createdAtUtc ? new Date(usa.createdAtUtc).toLocaleDateString() : 'Unknown',
        status: usa.user?.status ?? 3, // Map status field (default to Active if undefined)
        termsAccepted: usa.user?.hasAcceptedTerms ?? true,
        authorizations: {
          tasks: usa.tasksAuthorization || false,
          entities: usa.entitiesAuthorization || false,
          documents: usa.documentsAuthorization || false,
          invoices: usa.invoicesAuthorization || false,
          messages: usa.messagesAuthorization || false,
          shares: usa.sharesAuthorization || false,
          relatedParties: usa.relatedPartiesAuthorization || false,
          requests: usa.requestsAuthorization || false,
          banking: usa.bankingAuthorization || false,
          assets: usa.assetsAuthorization || false,
          investments: usa.investmentsAuthorization || false,
          information: usa.informationAuthorization || false,
          loans: usa.loansAuthorization || false
        }
      }))),
      catchError((error: HttpErrorResponse) =>
        throwError(() => new Error(
          error.error?.message || 'Failed to fetch site users'
        ))
      )
    );
  }

  /**
   * Generate initials from a full name
   */
  private generateInitials(fullName: string): string {
    if (!fullName) return '??';

    const names = fullName.trim().split(' ');
    if (names.length === 1) {
      return names[0].substring(0, 2).toUpperCase();
    }

    return (names[0].charAt(0) + (names.at(-1)?.charAt(0) ?? '')).toUpperCase();  }

  /**
   * Get detailed summary for a specific entity
   */
  getEntityDetailSummary(entityCode: string, siteKey: string, siteInstanceKey: string): Observable<EntityDetailSummary> {
    const categoryCountsUrl = `${this.apiUrl}/sites/${siteKey}/instances/${siteInstanceKey}/entities/${entityCode}/category-counts`;

    // Get entity details and category counts in parallel
    // Entity details may fail if entity not in Entities table, so handle gracefully
    return forkJoin({
      entity: this.http.get<Record<string, string>>(`${this.apiUrl}/entities/${entityCode}`).pipe(
        catchError(() => {
          // Entity not found in Entities table, return default values
          return of({ code: entityCode, name: entityCode, type: 'Unknown' } as Record<string, string>);
        })
      ),
      categoryCounts: this.http.get<Record<string, number>>(categoryCountsUrl)
    }).pipe(
      map(({ entity, categoryCounts }) => ({
        id: entity.code || entityCode,
        name: entity.name || entityCode,
        type: entity.type || entity.baseType || 'Unknown',
        entityCode: entity.code || entityCode,
        totalEntities: categoryCounts['Current Entities'] || categoryCounts.currentEntities || categoryCounts.entities || categoryCounts.Entities || 0,
        totalResignedEntities: categoryCounts['Resigned Entities'] || categoryCounts.resignedEntities || categoryCounts.ResignedEntities || 0,
        totalInvoices: categoryCounts['Invoices'] || categoryCounts.invoices || categoryCounts.Invoices || 0,
        totalShareClasses: categoryCounts['Share Classes'] || categoryCounts.shareClasses || categoryCounts.ShareClasses || 0,
        totalBankAccounts: categoryCounts['Bank Accounts'] || categoryCounts.bankAccounts || categoryCounts.BankAccounts || 0,
        totalInvestments: categoryCounts['Investments'] || categoryCounts.investments || categoryCounts.Investments || 0,
        totalAssets: categoryCounts['Assets'] || categoryCounts.assets || categoryCounts.Assets || 0,
        totalLoans: categoryCounts['Loans'] || categoryCounts.loans || categoryCounts.Loans || 0,
        totalDocuments: categoryCounts['Documents'] || categoryCounts.documents || categoryCounts.Documents || 0
      })),
      catchError((error: HttpErrorResponse) =>
        throwError(() => new Error(
          error.error?.message || 'Failed to fetch entity summary'
        ))
      )
    );
  }


  /**
   * Generate a consistent avatar color based on email
   */
  private generateAvatarColor(email: string): string {
    if (!email) return '#6b7280'; // Default gray color

    const colors = [
      '#3b82f6', // Blue
      '#10b981', // Green
      '#f59e0b', // Yellow
      '#8b5cf6', // Purple
      '#ef4444', // Red
      '#06b6d4', // Cyan
      '#84cc16', // Lime
      '#f97316', // Orange
      '#ec4899', // Pink
      '#64748b'  // Slate
    ];

    // Simple hash function to get consistent color
    let hash = 0;
    for (let i = 0; i < email.length; i++) {
      hash = ((hash << 5) - hash) + (email.codePointAt(i) ?? 0);
      hash = hash & hash; // Convert to 32-bit integer
    }

    return colors[Math.abs(hash) % colors.length];
  }
}
